import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import {
  traiComplianceService, ConsentLookup, ConsentPage, ConsentRecord, ConsentShadowSummary,
  ConsentScope, ConsentSource, CONSENT_SOURCES,
} from 'api/services/traiCompliance.service';
import { formatWhen, isWouldRefuse, localInputToIso } from './traiRules';
import { Card, ImportPanel, Note, Pill, inputCls, labelCls, muted, th, td } from './shared';

const MODE_TEXT: Record<string, string> = {
  OFF: 'Off — consent is not looked up on calls.',
  SHADOW: 'Shadow — looked up after each automated call and recorded; nothing is refused.',
  ENFORCE: 'Enforce — promotional automated calls without consent are refused.',
};

/**
 * Item 3: consent provenance. Numbers go in whole and come back as the last four digits — the full
 * number is never stored. DND is unaffected: it is checked first, as always, and consent never
 * overrides it.
 */
const ConsentTab: React.FC = () => {
  const [summary, setSummary] = useState<ConsentShadowSummary | null>(null);
  const [page, setPage] = useState<ConsentPage | null>(null);
  const [phone, setPhone] = useState('');
  const [found, setFound] = useState<ConsentLookup | null>(null);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [s, p] = await Promise.all([traiComplianceService.consentShadow(30), traiComplianceService.consents(0, 50)]);
    if (s.success && s.data) setSummary(s.data);
    if (p.success && p.data) setPage(p.data);
    else if (!p.success) setNote({ ok: false, text: p.error || 'Could not load consent records' });
  }, []);
  useEffect(() => { load(); }, [load]);

  const lookup = async () => {
    setBusy('lookup'); setNote(null);
    const r = await traiComplianceService.lookupConsent(phone);
    setBusy(null);
    if (r.success && r.data) setFound(r.data);
    else { setFound(null); setNote({ ok: false, text: r.error || 'Lookup failed' }); }
  };

  const revoke = async (c: ConsentRecord) => {
    const reason = window.prompt(`Revoke consent ${c.number}? Reason (optional):`);
    if (reason === null) return;
    const r = await traiComplianceService.revokeConsent(c.id, reason || undefined);
    setNote(r.success ? { ok: true, text: 'Revoked' } : { ok: false, text: r.error || 'Could not revoke' });
    load();
    if (found) lookup();
  };

  const wouldRefuse = summary
    ? Object.entries(summary.byVerdict).filter(([v]) => isWouldRefuse(v)).reduce((n, [, c]) => n + c, 0) : 0;

  return (
    <div className="space-y-4">
      <Note note={note} />
      <Card title="Shadow result — last 30 days" subtitle={summary ? MODE_TEXT[summary.mode] : ''}>
        {!summary ? <p className="text-sm" style={muted}>Loading…</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
              <p className="text-2xl font-semibold">{wouldRefuse.toLocaleString()}</p>
              <p className="text-sm" style={muted}>automated calls would have been refused: no consent (promotional)</p>
            </div>
            {Object.entries(summary.byVerdict).filter(([v]) => !isWouldRefuse(v)).map(([v, n]) => (
              <div key={v} className="rounded-lg bg-gray-50 dark:bg-gray-800 p-4">
                <p className="text-2xl font-semibold">{n.toLocaleString()}</p>
                <p className="text-sm" style={muted}>{v}</p>
              </div>
            ))}
            {summary.mode === 'OFF' && (
              <p className="text-sm sm:col-span-3" style={muted}>Consent mode is off on the server, so no calls are being checked yet.</p>
            )}
          </div>
        )}
      </Card>

      <Card title="Look up a number">
        <div className="flex gap-2">
          <input className={inputCls} placeholder="+91…" value={phone} onChange={(e) => setPhone(e.target.value)}
                 onKeyDown={(e) => { if (e.key === 'Enter' && phone.trim()) lookup(); }} />
          <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={!phone.trim() || busy === 'lookup'} onClick={lookup}>
            {busy === 'lookup' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />} Look up
          </button>
        </div>
        {found && (
          <div className="space-y-2">
            <p className="text-sm">
              {found.number}: promotional {found.activePromotional ? <Pill tone="success">consent</Pill> : <Pill tone="amber">none</Pill>}
              {' '}service {found.activeService ? <Pill tone="success">consent</Pill> : <Pill tone="amber">none</Pill>}
            </p>
            <ConsentTable rows={found.records} onRevoke={revoke} />
            {found.records.some((c) => c.active) && (
              <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={async () => {
                const r = await traiComplianceService.revokeNumber(phone);
                setNote(r.success ? { ok: true, text: `Revoked ${r.data?.revoked ?? 0} record(s)` } : { ok: false, text: r.error || 'Failed' });
                lookup(); load();
              }}>Revoke all for this number</button>
            )}
          </div>
        )}
      </Card>

      <RecordForm onSaved={(t) => { setNote({ ok: true, text: t }); load(); }} />

      <Card title="Import consent (CSV)" subtitle="Always previewed first.">
        <ImportPanel
          columns="phone,source,scope,captured_at,evidence_ref,expires_at"
          example="+919876543210,WEB_FORM,PROMOTIONAL,2026-10-01,https://example.com/form/123,"
          run={traiComplianceService.importConsents}
          onApplied={load}
        />
      </Card>

      <Card title={`Recent records${page ? ` (${page.totalElements})` : ''}`}>
        {page ? <ConsentTable rows={page.content} onRevoke={revoke} /> : <p className="text-sm" style={muted}>Loading…</p>}
      </Card>
    </div>
  );
};

const ConsentTable: React.FC<{ rows: ConsentRecord[]; onRevoke: (c: ConsentRecord) => void }> = ({ rows, onRevoke }) =>
  rows.length === 0 ? <p className="text-sm" style={muted}>No records.</p> : (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead><tr>
          <th className={th}>Number</th><th className={th}>Source</th><th className={th}>Scope</th><th className={th}>Captured</th>
          <th className={th}>Evidence</th><th className={th}>Status</th><th className={th} />
        </tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} className="border-t border-gray-100 dark:border-gray-800">
              <td className={`${td} font-mono`}>{c.number}</td>
              <td className={td}>{c.source}</td>
              <td className={td}>{c.scope}</td>
              <td className={td}>{formatWhen(c.capturedAt)}<div className="text-[11px]" style={muted}>{c.capturedBy}</div></td>
              <td className={`${td} text-xs break-all`}>{c.evidenceRef || '—'}</td>
              <td className={td}>
                {c.revokedAt ? <Pill tone="danger">revoked {formatWhen(c.revokedAt)}</Pill>
                  : c.active ? <Pill tone="success">active</Pill> : <Pill tone="amber">expired</Pill>}
              </td>
              <td className={td}>{!c.revokedAt && <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => onRevoke(c)}>Revoke</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

const RecordForm: React.FC<{ onSaved: (t: string) => void }> = ({ onSaved }) => {
  const [phone, setPhone] = useState('');
  const [source, setSource] = useState<ConsentSource>('WEB_FORM');
  const [scope, setScope] = useState<ConsentScope>('ALL');
  const [capturedAt, setCapturedAt] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    const r = await traiComplianceService.recordConsent({
      phone, source, scope, capturedAt: localInputToIso(capturedAt), expiresAt: localInputToIso(expiresAt),
      evidenceRef: evidenceRef || undefined,
    });
    setBusy(false);
    if (r.success) { setPhone(''); setEvidenceRef(''); onSaved(`Consent recorded for ${r.data?.number || 'the number'}`); }
    else setErr(r.error || 'Could not record');
  };

  return (
    <Card title="Record consent">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label><span className={labelCls} style={muted}>Phone</span>
          <input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+91…" /></label>
        <label><span className={labelCls} style={muted}>Source</span>
          <select className={inputCls} value={source} onChange={(e) => setSource(e.target.value as ConsentSource)}>
            {CONSENT_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select></label>
        <label><span className={labelCls} style={muted}>Scope</span>
          <select className={inputCls} value={scope} onChange={(e) => setScope(e.target.value as ConsentScope)}>
            <option value="ALL">All calls</option><option value="PROMOTIONAL">Promotional</option><option value="SERVICE">Service</option>
          </select></label>
        <label><span className={labelCls} style={muted}>Captured at (blank = now)</span>
          <input type="datetime-local" className={inputCls} value={capturedAt} onChange={(e) => setCapturedAt(e.target.value)} /></label>
        <label><span className={labelCls} style={muted}>Expires (optional)</span>
          <input type="datetime-local" className={inputCls} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} /></label>
        <label><span className={labelCls} style={muted}>Evidence (URL / id)</span>
          <input className={inputCls} value={evidenceRef} maxLength={500} onChange={(e) => setEvidenceRef(e.target.value)} /></label>
      </div>
      {err && <p className="text-sm text-red-700">{err}</p>}
      <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={!phone.trim() || busy} onClick={save}>
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Record'}
      </button>
    </Card>
  );
};

export default ConsentTab;
